// ─── MktSelector.jsx ─────────────────────────────────────────────────────
// Single-button dropdown with checkboxes for All / each marketplace / each
// shared inventory pool. Used by Targets and Cash Calendar to replace the
// inline pill row with a more compact, scannable selector that matches the
// overall app design (rounded button + popover, theme-aware).
//
// Selection model — same as Dashboard:
//   selMkts === "all"             → all marketplaces selected
//   selMkts === Array of mkt IDs  → specific subset
//
// Props:
//   marketplaces      [{ id, name, ...}]
//   inventoryPools    [{ id, name, marketplaceIds, isShared }]   (only shared
//                      pools render as one-click shortcuts)
//   selMkts           "all" | string[]
//   setSelMkts        (next) => void
//   compact           boolean — small button variant for tight toolbars

import { useState, useEffect, useRef } from "react";
import { useTheme } from "../lib/ThemeContext.jsx";
import { FONT, RADIUS, SPRING } from "../lib/theme.js";

export default function MktSelector({ marketplaces = [], inventoryPools = [], selMkts, setSelMkts, compact = false }) {
  const T = useTheme();
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    function onDocMouseDown(e) {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    }
    document.addEventListener("mousedown", onDocMouseDown);
    return () => document.removeEventListener("mousedown", onDocMouseDown);
  }, [open]);

  const isAll = selMkts === "all" || !Array.isArray(selMkts) || !selMkts.length;
  const selSet = new Set(Array.isArray(selMkts) ? selMkts : []);

  // Trigger label — concise summary so the button stays narrow
  const triggerLabel = (() => {
    if (isAll) return "All Marketplaces";
    if (selMkts.length === 1) {
      const m = marketplaces.find(x => x.id === selMkts[0]);
      return m?.name || "1 marketplace";
    }
    return `${selMkts.length} marketplaces`;
  })();

  function toggleMkt(id) {
    if (isAll) { setSelMkts([id]); return; }
    if (selSet.has(id)) {
      const next = (Array.isArray(selMkts) ? selMkts : []).filter(x => x !== id);
      setSelMkts(next.length ? next : "all");
    } else {
      setSelMkts([...(Array.isArray(selMkts) ? selMkts : []), id]);
    }
  }

  function pickAll() { setSelMkts("all"); }
  function pickPool(p) { setSelMkts([...(p.marketplaceIds || [])]); }

  // Pool-pick selected state — true when current selection exactly matches
  // the pool's marketplaceIds
  function poolIsSelected(p) {
    const ids = p.marketplaceIds || [];
    if (isAll) return false;
    if (selMkts.length !== ids.length) return false;
    return ids.every(id => selSet.has(id));
  }

  const sharedPools = (inventoryPools || []).filter(p => p.isShared && (p.marketplaceIds || []).length > 0);

  const padY = compact ? 6 : 8;
  const padX = compact ? 12 : 14;
  const fontSize = compact ? 12 : 13;

  return (
    <div ref={wrapRef} style={{ position: "relative", display: "inline-block" }}>
      <button
        onClick={() => setOpen(o => !o)}
        style={{
          padding: `${padY}px ${padX}px`,
          borderRadius: RADIUS.pill,
          fontSize,
          fontFamily: FONT,
          cursor: "pointer",
          fontWeight: 700,
          background: open ? T.accent + "22" : "transparent",
          border: `1px solid ${open ? T.accent : T.border}`,
          color: open ? T.accent : T.text,
          display: "inline-flex",
          alignItems: "center",
          gap: 6,
          whiteSpace: "nowrap",
          transition: `all 0.18s ${SPRING}`,
        }}
      >
        <span>📊 {triggerLabel}</span>
        <span style={{ fontSize: 9, opacity: 0.7, transform: open ? "rotate(180deg)" : "rotate(0deg)", transition: `transform 0.18s` }}>▼</span>
      </button>
      {open && (
        <div
          role="menu"
          style={{
            position: "absolute",
            top: "calc(100% + 6px)",
            left: 0,
            zIndex: 999,
            minWidth: 240,
            maxHeight: 360,
            overflowY: "auto",
            background: T.card,
            border: `1px solid ${T.border}`,
            borderRadius: RADIUS.md,
            boxShadow: `0 12px 32px rgba(0,0,0,0.32)`,
            padding: 6,
            animation: `sfFadeIn 0.15s ease both`,
          }}
        >
          {/* All */}
          <button
            onClick={() => { pickAll(); setOpen(false); }}
            style={{
              display: "flex", alignItems: "center", gap: 8,
              width: "100%", padding: "8px 10px",
              background: isAll ? T.accent + "18" : "transparent",
              border: "none", borderRadius: RADIUS.sm,
              color: isAll ? T.accent : T.text,
              fontSize: 13, fontFamily: FONT, fontWeight: isAll ? 700 : 500,
              cursor: "pointer", textAlign: "left",
            }}
          >
            <span style={{
              width: 14, height: 14, borderRadius: 4,
              border: `1.5px solid ${isAll ? T.accent : T.border}`,
              background: isAll ? T.accent : "transparent",
              display: "inline-flex", alignItems: "center", justifyContent: "center",
              flexShrink: 0,
            }}>
              {isAll && <span style={{ color: "#fff", fontSize: 10, fontWeight: 900 }}>✓</span>}
            </span>
            <span>All Marketplaces</span>
          </button>

          {/* Shared pool shortcuts */}
          {sharedPools.length > 0 && (
            <>
              <div style={{ fontSize: 9, fontWeight: 700, color: T.textSub, padding: "6px 10px 4px", letterSpacing: 0.5, textTransform: "uppercase" }}>
                🔗 Shared Pools
              </div>
              {sharedPools.map(p => {
                const sel = poolIsSelected(p);
                return (
                  <button
                    key={p.id}
                    onClick={() => { pickPool(p); }}
                    title={`Selects all ${p.marketplaceIds.length} member marketplaces`}
                    style={{
                      display: "flex", alignItems: "center", gap: 8,
                      width: "100%", padding: "8px 10px",
                      background: sel ? T.accent + "18" : "transparent",
                      border: "none", borderRadius: RADIUS.sm,
                      color: sel ? T.accent : T.text,
                      fontSize: 13, fontFamily: FONT, fontWeight: sel ? 700 : 500,
                      cursor: "pointer", textAlign: "left",
                    }}
                  >
                    <span style={{
                      width: 14, height: 14, borderRadius: 4,
                      border: `1.5px solid ${sel ? T.accent : T.border}`,
                      background: sel ? T.accent : "transparent",
                      display: "inline-flex", alignItems: "center", justifyContent: "center",
                      flexShrink: 0,
                    }}>
                      {sel && <span style={{ color: "#fff", fontSize: 10, fontWeight: 900 }}>✓</span>}
                    </span>
                    <span>🔗 {p.name}</span>
                    <span style={{ fontSize: 10, color: T.textSub, marginLeft: "auto" }}>{p.marketplaceIds.length}</span>
                  </button>
                );
              })}
            </>
          )}

          {/* Individual marketplaces */}
          {marketplaces.length > 0 && (
            <>
              <div style={{ fontSize: 9, fontWeight: 700, color: T.textSub, padding: "6px 10px 4px", letterSpacing: 0.5, textTransform: "uppercase" }}>
                Marketplaces
              </div>
              {marketplaces.map(m => {
                const sel = !isAll && selSet.has(m.id);
                return (
                  <button
                    key={m.id}
                    onClick={() => toggleMkt(m.id)}
                    style={{
                      display: "flex", alignItems: "center", gap: 8,
                      width: "100%", padding: "8px 10px",
                      background: sel ? T.accent + "18" : "transparent",
                      border: "none", borderRadius: RADIUS.sm,
                      color: sel ? T.accent : T.text,
                      fontSize: 13, fontFamily: FONT, fontWeight: sel ? 700 : 500,
                      cursor: "pointer", textAlign: "left",
                    }}
                  >
                    <span style={{
                      width: 14, height: 14, borderRadius: 4,
                      border: `1.5px solid ${sel ? T.accent : T.border}`,
                      background: sel ? T.accent : "transparent",
                      display: "inline-flex", alignItems: "center", justifyContent: "center",
                      flexShrink: 0,
                    }}>
                      {sel && <span style={{ color: "#fff", fontSize: 10, fontWeight: 900 }}>✓</span>}
                    </span>
                    <span>{m.name}</span>
                  </button>
                );
              })}
            </>
          )}
        </div>
      )}
    </div>
  );
}
